<script setup lang="ts">
import { computed, ref } from 'vue'
import type { FormFieldDef, SelectOption } from '@/types/page'
import ProSelect from './ProSelect.vue'
import AppIcon from '@/components/AppIcon.vue'

const props = withDefaults(defineProps<{
  field: FormFieldDef
  modelValue?: unknown
  options?: SelectOption[]
  error?: string
  disabled?: boolean
  inline?: boolean
}>(), {
  modelValue: () => '',
  options: () => [],
  error: '',
  disabled: false,
  inline: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: unknown]
  blur: []
}>()

const fileInput = ref<HTMLInputElement | null>(null)
const localFileName = ref('')

const value = computed({
  get: () => props.modelValue,
  set: (next: unknown) => emit('update:modelValue', next),
})

const inputType = computed(() => {
  if (props.field.type === 'password') return 'password'
  if (props.field.type === 'email') return 'email'
  if (props.field.type === 'phone') return 'tel'
  return 'text'
})

const currentOptions = computed(() => props.options.length ? props.options : (props.field.options ?? []))
const isFull = computed(() => props.field.full || ['textarea', 'file'].includes(props.field.type))
const isReadonly = computed(() => props.field.type === 'readonly')
const fileAccept = computed(() => props.field.type === 'image' ? 'image/png,image/jpeg,image/webp' : '.png,.jpg,.jpeg,.webp,.pdf,.mp4')
const displayFile = computed(() => localFileName.value || String(props.modelValue ?? ''))

function pickFile(): void {
  fileInput.value?.click()
}

function onFile(event: Event): void {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return
  localFileName.value = file.name
  emit('update:modelValue', file.name)
  target.value = ''
}

function onNumber(next: number | undefined): void {
  emit('update:modelValue', typeof next === 'number' ? next : '')
}
</script>

<template>
  <div class="pro-field" :class="{ 'pro-field--full': isFull, 'pro-field--inline': inline, 'has-error': error }">
    <label class="pro-field__label" :for="`field-${field.key}`">
      <span>{{ field.label }}</span>
      <em v-if="field.required" aria-hidden="true">*</em>
    </label>
    <div class="pro-field__control">
      <ProSelect
        v-if="['select', 'region', 'user'].includes(field.type)"
        :model-value="String(value ?? '')"
        :options="currentOptions"
        :placeholder="field.placeholder || `请选择${field.label}`"
        :disabled="disabled || isReadonly"
        :aria-label="field.label"
        :input-id="`field-${field.key}`"
        @update:model-value="value = $event"
        @blur="emit('blur')"
      />
      <ProSelect
        v-else-if="field.type === 'multiselect' || field.type === 'tags'"
        :model-value="Array.isArray(value) ? value.map(String) : []"
        :options="currentOptions"
        :placeholder="field.placeholder || `请选择${field.label}`"
        :multiple="true"
        :clearable="true"
        :disabled="disabled"
        :aria-label="field.label"
        :input-id="`field-${field.key}`"
        @update:model-value="value = $event"
        @blur="emit('blur')"
      />
      <el-input
        v-else-if="field.type === 'textarea'"
        v-model="value as string"
        type="textarea"
        :rows="3"
        :maxlength="field.maxLength"
        show-word-limit
        :placeholder="field.placeholder || `请输入${field.label}`"
        :disabled="disabled"
        :aria-label="field.label"
        @blur="emit('blur')"
      />
      <el-input-number
        v-else-if="field.type === 'number'"
        :model-value="typeof value === 'number' ? value : undefined"
        :min="field.min"
        :max="field.max"
        :step="field.step ?? 1"
        :controls="false"
        :disabled="disabled"
        :placeholder="field.placeholder"
        :aria-label="field.label"
        @update:model-value="onNumber"
      />
      <el-input-number
        v-else-if="field.type === 'money'"
        :model-value="typeof value === 'number' ? value : undefined"
        :min="field.min ?? 0"
        :precision="2"
        :step="1"
        :controls="false"
        :disabled="disabled"
        :placeholder="field.placeholder || '请输入金额（元）'"
        :aria-label="field.label"
        @update:model-value="onNumber"
      />
      <el-date-picker
        v-else-if="field.type === 'date'"
        v-model="value as string"
        type="date"
        value-format="YYYY-MM-DD"
        :placeholder="field.placeholder || `请选择${field.label}`"
        :disabled="disabled"
        :aria-label="field.label"
        class="pro-field__date"
      />
      <el-date-picker
        v-else-if="field.type === 'datetime'"
        v-model="value as string"
        type="datetime"
        value-format="YYYY-MM-DD HH:mm:ss"
        :placeholder="field.placeholder || `请选择${field.label}`"
        :disabled="disabled"
        :aria-label="field.label"
        class="pro-field__date"
      />
      <el-switch
        v-else-if="field.type === 'switch'"
        v-model="value as boolean"
        :disabled="disabled"
        :aria-label="field.label"
      />
      <div v-else-if="field.type === 'file' || field.type === 'image'" class="pro-field__file">
        <button type="button" class="file-picker" :disabled="disabled" @click="pickFile">
          <span class="file-picker__icon"><AppIcon :name="field.type === 'image' ? 'image' : 'upload'" :size="17" /></span>
          <span class="file-picker__text">
            <b>{{ displayFile || (field.type === 'image' ? '选择图片' : '选择文件') }}</b>
            <small>{{ displayFile ? '已选择，点击可重新选择' : '点击选择本地文件，仅在当前原型中展示' }}</small>
          </span>
        </button>
        <input ref="fileInput" type="file" hidden :accept="fileAccept" @change="onFile" />
      </div>
      <div v-else-if="isReadonly" class="pro-field__readonly">
        {{ value || field.placeholder || '保存后自动生成' }}
      </div>
      <el-input
        v-else
        v-model="value as string"
        :type="inputType"
        :maxlength="field.maxLength"
        :placeholder="field.placeholder || `请输入${field.label}`"
        :disabled="disabled || isReadonly"
        :aria-label="field.label"
        @blur="emit('blur')"
      />
      <p v-if="field.help" class="pro-field__help">{{ field.help }}</p>
      <p v-if="error" class="pro-field__error" role="alert"><AppIcon name="warning" :size="12" />{{ error }}</p>
    </div>
  </div>
</template>

<style scoped>
.pro-field { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
.pro-field--full { grid-column: span 2; }
.pro-field__label { display: flex; align-items: center; gap: 3px; font-size: 11.5px; font-weight: 600; color: #40536c; line-height: 1.4; }
.pro-field__label em { color: var(--c-danger); font-style: normal; }
.pro-field__control { min-width: 0; }
.pro-field__help { margin-top: 4px; font-size: 10px; color: var(--c-muted); line-height: 1.5; }
.pro-field__error { display: flex; align-items: center; gap: 3px; margin-top: 3px; font-size: 10px; color: var(--c-danger); line-height: 1.4; }
.pro-field.has-error :deep(.el-input__wrapper), .pro-field.has-error :deep(.el-select__wrapper) { box-shadow: 0 0 0 1px var(--c-danger) inset; }
.pro-field__date { width: 100%; }
.pro-field__readonly { min-height: 34px; display: flex; align-items: center; padding: 0 10px; border-radius: 8px; border: 1px dashed var(--c-border-strong); background: var(--c-surface-muted); color: var(--c-muted); font-size: 12px; }
.pro-field__file { width: 100%; }
.file-picker { width: 100%; min-height: 58px; display: flex; align-items: center; gap: 10px; padding: 9px 11px; border: 1px dashed var(--c-border-strong); border-radius: 9px; background: var(--c-surface-muted); text-align: left; }
.file-picker:hover { border-color: var(--c-brand); background: var(--c-brand-mist); }
.file-picker:disabled { opacity: .55; cursor: not-allowed; }
.file-picker__icon { width: 30px; height: 30px; border-radius: 8px; background: #fff; display: grid; place-items: center; color: var(--c-brand-strong); border: 1px solid var(--c-border); }
.file-picker__text { display: flex; flex-direction: column; min-width: 0; }
.file-picker__text b { font-size: 11.5px; color: var(--c-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.file-picker__text small { margin-top: 1px; font-size: 9.5px; color: var(--c-muted); }
.pro-field--inline { gap: 4px; }
@media (max-width: 767px) { .pro-field--full { grid-column: span 1; } }
</style>
